/**
 * Primitives, envelopes and enums shared by every domain schema.
 * Names match `components.schemas` in docs/specs/openapi-and-graphql.yaml.
 */
import { z } from 'zod';
import type { PermissionBitName } from '../permissions.js';

// ---------------------------------------------------------------------------
// Primitives (not OpenAPI components; helpers reused across schemas)
// ---------------------------------------------------------------------------

/** UUID (v7 in practice; any RFC 4122 form is accepted). */
export const Id = z.uuid();
export type Id = z.infer<typeof Id>;

/** ISO-8601 UTC timestamp (`Z` or offset). */
export const Timestamp = z.iso.datetime({ offset: true });
export type Timestamp = z.infer<typeof Timestamp>;

/** ISO-8601 calendar date `YYYY-MM-DD`. */
export const DateOnly = z.iso.date();
export type DateOnly = z.infer<typeof DateOnly>;

export const Uri = z.url();
export const Email = z.email();
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);
export const Slug = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{1,62}[a-z0-9])?$/);
/** Share-link / guest-form token: 32 random bytes, base64url (43 chars). */
export const ShareToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const Int64 = z.int().min(0);
export const Percent = z.int().min(0).max(100);
export const UnitInterval = z.number().min(0).max(1);
/** Free-form JSON object (`additionalProperties: true`). */
export const JsonObject = z.record(z.string(), z.unknown());

/** `minProperties: 1` for PATCH bodies. */
export function atLeastOneField<T extends z.ZodObject>(schema: T): T {
  return schema.refine(
    (o) => Object.values(o as Record<string, unknown>).some((v) => v !== undefined),
    {
      message: 'at least one field is required',
    },
  ) as T;
}

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

export const ErrorResponse = z.object({
  code: z.string(),
  message: z.string(),
  details: JsonObject.optional(),
  request_id: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const CursorPage = z.object({
  next_cursor: z.string().nullable(),
  total: z.int().nullable().optional(),
});
export type CursorPage = z.infer<typeof CursorPage>;

/** `allOf: [CursorPage, {items: T[]}]` — generic page helper. */
export function cursorPage<T extends z.ZodType>(item: T) {
  return CursorPage.extend({ items: z.array(item) });
}
export type CursorPageOf<T> = CursorPage & { items: T[] };

// ---------------------------------------------------------------------------
// Permission primitives
// ---------------------------------------------------------------------------

export const PermissionBit = z.enum([
  'VIEW',
  'DOWNLOAD',
  'COMMENT',
  'UPLOAD',
  'EDIT_METADATA',
  'MANAGE_BOARD',
  'MANAGE_MEMBERS',
  'ADMIN',
  'SHARE',
  'APPROVE',
  'DELETE_ANY_COMMENT',
] satisfies readonly PermissionBitName[]);
export type PermissionBit = z.infer<typeof PermissionBit>;

/**
 * Wire form of a mask: a JSON integer (OpenAPI `integer/int64`). JSON numbers are exact only
 * up to 2^53-1, which covers every defined bit (0–10) with room to spare. Convert to bigint with
 * `parseMask()` from `permissions.ts` before doing bit math.
 */
export const PermissionMask = z.int().min(0).max(Number.MAX_SAFE_INTEGER);
export type PermissionMask = z.infer<typeof PermissionMask>;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const AssetType = z.enum(['image', 'video', 'audio', 'document', 'other']);
export type AssetType = z.infer<typeof AssetType>;

export const ApprovalStatus = z.enum(['none', 'in_progress', 'needs_review', 'approved']);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

export const VersionState = z.enum(['uploading', 'queued', 'processing', 'ready', 'failed']);
export type VersionState = z.infer<typeof VersionState>;

export const MemberStatus = z.enum(['invited', 'active', 'suspended']);
export type MemberStatus = z.infer<typeof MemberStatus>;

export const TagSource = z.enum(['user', 'ai']);
export type TagSource = z.infer<typeof TagSource>;

export const CoverMode = z.enum(['auto', 'manual']);
export type CoverMode = z.infer<typeof CoverMode>;

export const HashKind = z.enum(['full', 'sampled']);
export type HashKind = z.infer<typeof HashKind>;

export const ShareTargetType = z.enum(['board', 'asset']);
export type ShareTargetType = z.infer<typeof ShareTargetType>;

export const CustomFieldType = z.enum(['text', 'select', 'multi_select', 'date', 'number']);
export type CustomFieldType = z.infer<typeof CustomFieldType>;

export const BoardAssetSort = z
  .enum([
    'added_at:desc',
    'added_at:asc',
    'name:asc',
    'name:desc',
    'size:desc',
    'size:asc',
    'created_at:desc',
    'created_at:asc',
    'sort_order:asc',
  ])
  .default('added_at:desc');
export type BoardAssetSort = z.infer<typeof BoardAssetSort>;

// --- release2 (contract) --- enums referenced by core schemas (Board, AssetCard, Asset); see release2.ts
export const BoardKind = z.enum(['static', 'smart']);
export type BoardKind = z.infer<typeof BoardKind>;

/** Default sort of a smart board's listing (no `added_at` / `sort_order` without membership). */
export const SmartBoardSort = z.enum([
  'created_at:desc',
  'created_at:asc',
  'name:asc',
  'name:desc',
  'size:desc',
  'size:asc',
]);
export type SmartBoardSort = z.infer<typeof SmartBoardSort>;

export const RightsLicense = z.enum([
  'royalty_free',
  'rights_managed',
  'editorial',
  'creative_commons',
  'owned',
  'other',
]);
export type RightsLicense = z.infer<typeof RightsLicense>;

/** Model or property release. */
export const ReleaseStatus = z.enum(['not_required', 'pending', 'obtained', 'missing']);
export type ReleaseStatus = z.infer<typeof ReleaseStatus>;

/** Derived: `none` (no expiry), `valid`, `expiring` (within 30 days), `expired`. */
export const RightsStatus = z.enum(['none', 'valid', 'expiring', 'expired']);
export type RightsStatus = z.infer<typeof RightsStatus>;

/** Days ahead that count as `expiring` (RightsStatus, the `rights` facet, the Expiring system board). */
export const RIGHTS_EXPIRING_DAYS = 30;

// --- release4/R --- META-3: Creative Commons variant, allowed only while `license = creative_commons`
export const CcVariant = z.enum(['by', 'by-sa', 'by-nc', 'by-nc-sa', 'by-nd', 'by-nc-nd', 'zero']);
export type CcVariant = z.infer<typeof CcVariant>;

// --- release3 (contract) --- enums referenced by core schemas (Asset, ShareLink, Board, Member, Job); see release3.ts
export const NotificationKind = z.enum([
  'mention',
  'reply',
  'comment',
  'approval',
  'upload',
  'share_opened',
  'share_downloaded',
  'guest_upload',
  'board_invite',
  'rights_expired',
  'retention_applied',
]);
export type NotificationKind = z.infer<typeof NotificationKind>;

/** `off` = no row; `in_app` = inbox only; `email` = inbox + mail now; `digest` = inbox + bundled mail. */
export const NotificationChannel = z.enum(['off', 'in_app', 'email', 'digest']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

/** Which versions a share link serves: the active one (default), the pinned one, or every live version. */
export const ShareVersionMode = z.enum(['current', 'pinned', 'all']);
export type ShareVersionMode = z.infer<typeof ShareVersionMode>;

export const BoardVisibility = z.enum(['workspace', 'private']);
export type BoardVisibility = z.infer<typeof BoardVisibility>;

export const ExportFormat = z.enum(['original', 'jpeg', 'png', 'webp', 'avif']);
export type ExportFormat = z.infer<typeof ExportFormat>;

/** Guest override level on a board: view = VIEW, comment = VIEW|COMMENT, upload = VIEW|COMMENT|UPLOAD. */
export const GuestAccess = z.enum(['view', 'comment', 'upload']);
export type GuestAccess = z.infer<typeof GuestAccess>;

export const DominantColor = z.object({
  hex: HexColor,
  pct: UnitInterval,
});
export type DominantColor = z.infer<typeof DominantColor>;

export const ActorKind = z.enum(['member', 'guest', 'system']);
export type ActorKind = z.infer<typeof ActorKind>;

export const ActorRef = z.object({
  kind: ActorKind.optional(),
  member_id: Id.nullable().optional(),
  display_name: z.string().nullable().optional(),
  avatar_url: Uri.nullable().optional(),
  guest_name: z.string().nullable().optional(),
});
export type ActorRef = z.infer<typeof ActorRef>;
